/**
 * @fileoverview Unlock Status Service - Tracks whether user has unlocked in current session
 *
 * Persists unlock status to IndexedDB so that across page reloads/navigations,
 * we can skip showing the unlock dialog if the user already unlocked.
 *
 * @module services/UnlockStatusService
 */

import { createLogger } from '@xanectech/lib';

const logger = createLogger('UnlockStatusService');

const DB_NAME = 'XanecTechUnlockStatus';
const DB_VERSION = 1;
const STORE_NAME = 'unlock_status';

interface UnlockRecord {
  userId: string;
  isUnlocked: boolean;
  unlockedAt: number; // Timestamp
  expiresAt: number | null; // Timestamp when unlock expires, or null for never expire
}

/**
 * Initialize IndexedDB for unlock status
 */
async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () =>
      reject(new Error('Failed to open unlock status DB'));
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
  });
}

/**
 * Mark user as unlocked
 * @param userId - User ID
 * @param durationMs - How long the unlock is valid (default: 1 hour, null = never expire)
 */
export async function markUserUnlocked(
  userId: string,
  durationMs: number | null = 60 * 60 * 1000, // 1 hour default, null means never expire
): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const now = Date.now();
    const expiresAt = durationMs === null ? null : now + durationMs;

    const record: UnlockRecord = {
      userId,
      isUnlocked: true,
      unlockedAt: now,
      expiresAt,
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(record);
      request.onsuccess = () => {
        db.close();
        resolve();
      };
      request.onerror = () => {
        db.close();
        reject(new Error('Failed to mark user as unlocked'));
      };
    });
  } catch (error) {
    logger.error('Error marking user as unlocked', error as Error);
    // Don't throw - allow app to continue even if IndexedDB fails
  }
}

/**
 * Check if user is currently unlocked
 * @param userId - User ID
 * @returns True if user is unlocked and not expired
 */
export async function isUserUnlocked(userId: string): Promise<boolean> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise<boolean>((resolve, reject) => {
      const request = store.get(userId);
      request.onsuccess = () => {
        const record = request.result as UnlockRecord | undefined;
        db.close();

        if (!record || !record.isUnlocked) {
          resolve(false);
          return;
        }

        // Check if expired (null expiresAt means never expire)
        if (record.expiresAt !== null && Date.now() > record.expiresAt) {
          resolve(false);
          return;
        }

        resolve(true);
      };
      request.onerror = () => {
        db.close();
        reject(new Error('Failed to check unlock status'));
      };
    });
  } catch (error) {
    logger.error('Error checking unlock status', error as Error);
    return false; // Default to locked on error
  }
}

/**
 * Clear unlock status for a user
 * @param userId - User ID
 */
export async function clearUnlockStatus(userId: string): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(userId);
      request.onsuccess = () => {
        db.close();
        resolve();
      };
      request.onerror = () => {
        db.close();
        reject(new Error('Failed to clear unlock status'));
      };
    });
  } catch (error) {
    logger.error('Error clearing unlock status', error as Error);
    // Don't throw
  }
}
